import React from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { QueryClientProvider } from "@tanstack/react-query";
import { setDefaultOptions } from "date-fns";
import { pt } from "date-fns/locale";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/contexts/auth-context";
import { ClientProfileProvider } from "@/contexts/client-profile-context";
import { PermissionProvider } from "@/contexts/permission-context";
import { registerSW } from "virtual:pwa-register";
import { iniciarVigiaDeVersao } from "@/lib/app-update";
import { installLogCapture } from "@/lib/error-report";
import { initObservability } from "@/lib/observability";
import { queryClient } from "@/lib/react-query";
import { ReportProblemHost } from "@/components/support/report-problem-host";
import { AppErrorBoundary } from "@/components/support/app-error-boundary";
import App from "./App";
import "@silevis/reactgrid/styles.css";
import "./index.css";

// date-fns assume en-US por omissão: sem isto, um format() sem `{ locale: pt }`
// escreve "July"/"Monday" nos títulos. Fixa o português para toda a app.
setDefaultOptions({ locale: pt });

// Guarda em memória as últimas falhas do browser, para acompanharem um relatório
// de erro quando alguém carregar em «Enviar erro à equipa». Não envia nada sozinho.
installLogCapture();

// Sentry — captura erros não tratados em produção.
// No-op em dev (sem VITE_SENTRY_DSN).
initObservability();

// Service worker (PWA): põe a casca da app em cache para ela abrir sem rede —
// o que interessa na App do Segurança, numa portaria com ligação fraca.
// `autoUpdate`: a versão nova entra sozinha na próxima abertura, sem pedir nada
// ao utilizador (um segurança não deve ter de decidir sobre "há uma atualização").
//
// Procurar versão nova (de 10 em 10 min e ao voltar à app) e o que fazer sem SW: ver
// `lib/app-update.ts`.
let registoSW: ServiceWorkerRegistration | undefined;
registerSW({
  immediate: true,
  onRegisteredSW(_url, registration) {
    registoSW = registration;
  },
});
iniciarVigiaDeVersao({ obterRegisto: () => registoSW });

// Depois de um deploy, um separador aberto pode pedir um chunk (`/assets/x-<hash>.js`)
// que já não existe: o import dinâmico falha e a app fica em branco. Recarrega uma vez
// para apanhar a versão nova; o guarda evita um ciclo se o problema for outro.
window.addEventListener("vite:preloadError", (event) => {
  event.preventDefault();
  try {
    const ultimo = Number(sessionStorage.getItem("preload-reload") ?? 0);
    if (Date.now() - ultimo < 30_000) return;
    sessionStorage.setItem("preload-reload", String(Date.now()));
  } catch {
    // sem sessionStorage (modo privado): recarrega na mesma, uma só vez por carregamento
  }
  window.location.reload();
});

// Register Excel Configurator Adapters
import { registerExcelAdapters } from "@/features/excel-configurators";
registerExcelAdapters();

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <AppErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <ClientProfileProvider>
            <AuthProvider>
              <PermissionProvider>
                <App />
                <Toaster />
              <ReportProblemHost />
              </PermissionProvider>
            </AuthProvider>
          </ClientProfileProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </AppErrorBoundary>
  </React.StrictMode>,
);
